import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { formatPrintDate } from './dateUtils';

const TEAL = [13, 148, 136];
const GRAY = [55, 65, 81];
const GRAY_MUTED = [107, 114, 128];
const GRAY_LIGHT = [156, 163, 175];
const BG_TABLE_ALT = [249, 250, 251];
const MARGIN = 10;
const HEADER_FONT = 7.5;

const fmtQty = (value) =>
  parseFloat(value || 0).toLocaleString('es-PE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 3,
  });

function getStatusInfo(requirement) {
  if (requirement.status === 'cancelled') {
    return { label: 'Cancelado', tone: 'cancelled' };
  }
  const pct = requirement.fulfillment_percent ?? 0;
  if (pct >= 100) return { label: 'Completado', tone: 'completed' };
  if (pct > 0) return { label: 'Parcial', tone: 'partial' };
  return { label: 'Emitido', tone: 'issued' };
}

function measureSegments(doc, segments) {
  let width = 0;
  segments.forEach((seg) => {
    doc.setFont('helvetica', seg.style === 'label' ? 'bold' : 'normal');
    doc.setFontSize(HEADER_FONT);
    width += doc.getTextWidth(seg.text) + (seg.style === 'sep' ? 2.5 : 1.2);
  });
  return width;
}

function drawSegments(doc, startX, y, segments) {
  let x = startX;
  segments.forEach((seg) => {
    if (seg.style === 'label') {
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...TEAL);
    } else if (seg.style === 'sep') {
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...GRAY_LIGHT);
    } else {
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...GRAY);
    }
    doc.setFontSize(HEADER_FONT);
    doc.text(seg.text, x, y);
    x += doc.getTextWidth(seg.text) + (seg.style === 'sep' ? 2.5 : 1.2);
  });
  return x;
}

function drawStatusBadge(doc, x, y, statusText, tone) {
  doc.setFontSize(6);
  doc.setFont('helvetica', 'bold');
  const padX = 2;
  const textW = doc.getTextWidth(statusText);
  const badgeW = textW + padX * 2;
  const badgeH = 3.8;

  const tones = {
    issued: { fill: [209, 250, 229], text: [6, 95, 70] },
    partial: { fill: [254, 243, 199], text: [146, 64, 14] },
    completed: { fill: [219, 234, 254], text: [30, 64, 175] },
    cancelled: { fill: [254, 226, 226], text: [153, 27, 27] },
  };
  const colors = tones[tone] || tones.issued;

  doc.setFillColor(...colors.fill);
  doc.setTextColor(...colors.text);
  doc.roundedRect(x, y - badgeH + 1.2, badgeW, badgeH, 1.2, 1.2, 'F');
  doc.text(statusText, x + padX, y);
  return x + badgeW + 2;
}

function drawSectionTitle(doc, margin, y, title) {
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(...TEAL);
  doc.text(title.toUpperCase(), margin, y);
  doc.setDrawColor(...TEAL);
  doc.setLineWidth(0.3);
  doc.line(margin, y + 1.2, margin + doc.getTextWidth(title.toUpperCase()), y + 1.2);
}

function buildTableRows(requirement) {
  return (requirement.items || []).map((item) => {
    const description = item.product?.name || '—';
    const notes = item.notes ? `\nObs: ${item.notes}` : '';
    const project = item.project
      ? `${item.project.code || ''}${item.project.code && item.project.name ? ' - ' : ''}${item.project.name || ''}`
      : '—';

    return [
      `${description}${notes}`,
      item.unit_of_measure || item.product?.unit_of_measure || '—',
      project,
      fmtQty(item.quantity),
    ];
  });
}

function drawFooter(doc, requirement, startY, pageWidth, pageHeight) {
  const contentWidth = pageWidth - MARGIN * 2;
  const footerHeight = 14;

  let y = startY;
  if (y + footerHeight > pageHeight - MARGIN) {
    doc.addPage();
    y = MARGIN + 4;
  }

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7.5);
  doc.setTextColor(...TEAL);
  doc.text('Observaciones:', MARGIN, y + 4);

  if (requirement.notes) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(...GRAY);
    const noteLines = doc.splitTextToSize(String(requirement.notes), contentWidth);
    doc.text(noteLines.slice(0, 6), MARGIN, y + 8);
  }
}

export async function downloadRequirementPdf(requirement) {
  if (!requirement) {
    throw new Error('No hay datos del requerimiento para generar el PDF');
  }

  const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  let y = MARGIN + 2;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.setTextColor(...TEAL);
  doc.text('REQUERIMIENTO', pageWidth / 2, y, { align: 'center' });

  y += 2.5;
  doc.setDrawColor(...TEAL);
  doc.setLineWidth(0.5);
  doc.line(MARGIN, y, pageWidth - MARGIN, y);
  y += 5;

  const requestDate = formatPrintDate(requirement.request_date);
  const neededByDate = formatPrintDate(requirement.needed_by_date);
  const status = getStatusInfo(requirement);
  const pct = requirement.fulfillment_percent ?? 0;

  const line1 = [
    { text: 'Requerimiento:', style: 'label' },
    { text: requirement.code || '-', style: 'value' },
    { text: '·', style: 'sep' },
    { text: 'Fecha:', style: 'label' },
    { text: requestDate || '—', style: 'value' },
  ];
  if (neededByDate) {
    line1.push(
      { text: '·', style: 'sep' },
      { text: 'Fecha requerida:', style: 'label' },
      { text: neededByDate, style: 'value' },
    );
  }
  line1.push({ text: '·', style: 'sep' }, { text: 'Estado:', style: 'label' });
  const estadoX = MARGIN + measureSegments(doc, line1);
  drawSegments(doc, MARGIN, y, line1);
  drawStatusBadge(doc, estadoX, y, status.label, status.tone);

  y += 4;
  const supplierLine = [
    { text: 'Proveedor:', style: 'label' },
    { text: requirement.supplier?.name || 'Interno (sin proveedor)', style: 'value' },
    { text: '·', style: 'sep' },
    { text: 'Avance:', style: 'label' },
    { text: `${pct}%`, style: 'value' },
    { text: '·', style: 'sep' },
    { text: 'Ítems:', style: 'label' },
    { text: String(requirement.items?.length || 0), style: 'value' },
  ];
  drawSegments(doc, MARGIN, y, supplierLine);

  y += 6;
  drawSectionTitle(doc, MARGIN, y, 'Productos solicitados');
  y += 4;

  autoTable(doc, {
    startY: y,
    head: [['Descripción', 'Unidad', 'Proyecto', 'Cant.']],
    body: buildTableRows(requirement),
    margin: { left: MARGIN, right: MARGIN },
    theme: 'grid',
    styles: {
      font: 'helvetica',
      fontSize: 6,
      cellPadding: { top: 1, right: 2, bottom: 1, left: 2 },
      minCellHeight: 3.8,
      textColor: GRAY,
      lineColor: [229, 231, 235],
      lineWidth: 0.2,
      valign: 'top',
    },
    headStyles: {
      fillColor: TEAL,
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      fontSize: 6,
      halign: 'left',
    },
    columnStyles: {
      0: { cellWidth: 100 },
      1: { cellWidth: 22, halign: 'center' },
      2: { cellWidth: 48 },
      3: { cellWidth: 20, halign: 'right', fontStyle: 'bold' },
    },
    alternateRowStyles: { fillColor: BG_TABLE_ALT },
    didDrawPage: (data) => {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      doc.setTextColor(...GRAY_LIGHT);
      doc.text(
        `Requerimiento ${requirement.code || ''} — Pág. ${data.pageNumber}`,
        pageWidth / 2,
        pageHeight - 6,
        { align: 'center' },
      );
    },
  });

  const tableEndY = doc.lastAutoTable.finalY + 5;
  drawFooter(doc, requirement, tableEndY, pageWidth, pageHeight);

  const safeCode = String(requirement.code || requirement.id || 'requerimiento').replace(/[^\w.-]+/g, '_');
  doc.save(`Requerimiento_${safeCode}.pdf`);
}
